import {
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  TablePagination,
  Snackbar,
  Alert,
  AppBar,
  Toolbar,
} from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import Slide from "@mui/material/Slide";
import { makeStyles } from "@mui/styles";
import { v1 as uuidv1 } from "uuid";

//#Custom components
import moment from "moment";
import Helper from "../../common/Helper";

//#Service Apis
import {
  gameScoreBaseUrl,
  uploadGameScoreFileToBlob,
} from "../../services/AzureStorage";
import { AppDataContext } from "../../common/appContext";
import { GameScoreServices } from "../../services/gameScore.Services";

//#Icons
import JpgIcon from "../../sharedComponents/customIcons/JpgIcon";
import PngIcon from "../../sharedComponents/customIcons/PngIcon";
// import ExcelIcon from "../../sharedComponents/customIcons/ExcelIcon";
import TxtIcon from "../../sharedComponents/customIcons/TxtIcon";
import WordIcon from "../../sharedComponents/customIcons/WordIcon";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Loader from "../../sharedComponents/loader/loader";
import { Role } from "../../helper";

const useStyles = makeStyles((theme) => ({
  fileInput: {
    display: "flex",
    flex: "1",
    alignItems: "center",
  },
  headerBox: {
    background: "#c1d0ed",
  },
  headerText: {
    marginBottom: theme.spacing(0.5),
    color: "rgb(29, 66, 138)",
  },
  tableRow: {
    "&:nth-child(odd)": {
      background: "#f5f5f5",
    },
    "& .MuiTableCell-root": {
      borderBottom: "none",
    },
  },
  iconStyle: {
    width: "26px",
    height: "26px",
    cursor: "pointer",
  },
  appbarBackground: {
    backgroundColor: "#fff",
    color: "#1d428a",
  },
  title: {
    flexGrow: 1,
  },
}));

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const GameScoreDashboard = () => {
  const classes = useStyles();

  //#Get data from Context.
  const {
    selectedEventId,
    user,
    setSelectedEventId,
    setSelectedEvent,
    setCombineYear,
  } = useContext(AppDataContext);
  let userAppRoles = user.appRoles || [];

  //#Loader data holders
  const [loading, setLoading] = useState(false);

  //#Document data holders.
  const [displayFileName, setDisplayFileName] = useState(null);
  const [finalFileName, setFinalFileName] = useState(null);

  //#Document Listing  data holders.
  const [documentList, setDocumentList] = useState([]);

  //#Document Allowed extention  data holders.
  const [allowedFileExtensions, setAllowedFileExtensions] = useState([
    "png",
    "jpeg",
    "jpg",
    "pdf",
    "xlsx",
    "doc",
    "docx",
    "txt",
  ]);

  //#Document Dialog data holders.
  const [openPublishDialog, setOpenPublishDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  //#Document refs data holders.
  const elementRef = useRef();
  const elementFileRef = useRef(null);

  //#Pagination Data holders.
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [page, setPage] = React.useState(0);

  //#publish dialog msg
  const publishMsgRef = useRef(null);

  //#Alert data holders.
  const [alert, setAlert] = useState(false);
  const [alertType, setAlertType] = useState("warning");
  const [alertMessage, setAlertMessage] = useState("");

  //... Webview data holders
  const [isWebView, setIsWebView] = useState(false);

  useEffect(() => {
    setIsWebView(Helper.detectWebView());
  }, []);

  //... This effect will auto link shared event details to fetch details.
  useEffect(() => {
    if (isWebView && Object.keys(user).length > 0) {
      const eventId = sessionStorage.getItem("eventId");

      if (eventId) {
        const [combineYear, selectedEvent] = [
          eventId.slice(0, 4),
          eventId.slice(4),
        ];

        setCombineYear(combineYear);
        setSelectedEvent(selectedEvent);
        setSelectedEventId(eventId);
      }
    }
  }, [isWebView, user]);

  useEffect(() => {
    if (selectedEventId && Object.keys(user).length > 0) {
      GetUploadedGameScoreDocuments();
    }
  }, [selectedEventId, user]);

  const GetUploadedGameScoreDocuments = async () => {
    setLoading(true);
    setDocumentList([]);

    try {
      const result = await GameScoreServices.getUploadedDocument(
        selectedEventId,
        "Game Score"
      );

      const documentList =
        Object.keys(user).length > 0 && !user.isAdmin
          ? result?.data.filter((item) => item.isPublished === true)
          : result?.data;
      setDocumentList(documentList);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const uploadDocumentChangeHandler = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      let blob = file.slice(0, file.size, file.type);
      let extension = file.name.substring(file.name.lastIndexOf(".") + 1);

      //#Extention Validation
      if (!allowedFileExtensions.includes(extension.toLowerCase())) {
        setAlert(true);
        setAlertType("warning");
        setAlertMessage(
          `Please select files with one of ${allowedFileExtensions.join(
            ", "
          )} formats.`
        );
        clearFileInput();
        return;
      }
      //#Set Display filename.
      setDisplayFileName(file.name.replace(`.${extension}`, ""));

      //#Set new filename.
      let newFileName = uuidv1() + "." + extension;

      const newFile = new File([blob], newFileName, { type: file.type });
      setFinalFileName(newFile);
    }
  };

  const uploadDocumentHandler = (file) => {
    if (!file) {
      setAlert(true);
      setAlertType("warning");
      setAlertMessage("Please select document for upload.");
      return false;
    }

    (async () => {
      if (file) {
        try {
          //#Upload blob to azure storage
          const fileUploaded = await uploadGameScoreFileToBlob(file);
          if (fileUploaded) {
            //#Upload blob details in DB
            await GameScoreServices.uploadGameScoreDocument({
              docId: 0,
              eventId: selectedEventId,
              docType: "Game Score",
              fileExtension: file.name.substring(
                file.name.lastIndexOf(".") + 1
              ),
              documentName: file.name,
              displayName: displayFileName,
              isDeleted: false,
              isPublished: false,
              userBy: user.loginID,
              mode: "",
            }).then((result) => {
              if (result.data) {
                setAlert(true);
                setAlertType("success");
                setAlertMessage("Document uploaded successfully");

                //#fetch all updated document listing.
                GetUploadedGameScoreDocuments();

                //#Clear input
                clearFileInput();
              }
            });
          }
        } catch (e) {
          console.log("error", e);
        } finally {
          setLoading(false);
        }
      }
    })();
  };

  const updateDocumentDetails = async () => {
    setLoading(true);

    try {
      const result = await GameScoreServices.uploadGameScoreDocument(
        elementRef.current
      );
      if (result.data) {
        const alertMsg =
          elementRef.current.source === "delete"
            ? "Document deleted successfully"
            : "Document details updated successfully";

        setAlert(true);
        setAlertType("success");
        setAlertMessage(alertMsg);

        GetUploadedGameScoreDocuments();
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setOpenPublishDialog(false);
  };

  const handleDialogAgree = () => {
    setOpenPublishDialog(false);
    updateDocumentDetails();
  };

  const handlePublishDocument = (obj, mode) => {
    //#Default publish Dialog message
    publishMsgRef.current = "Do you wish to Publish?";

    elementRef.current = {
      ...obj,
      mode: mode,
      isPublished: !obj.isPublished,
      source: "publish",
    };

    if (obj.isPublished) {
      publishMsgRef.current = "Do you wish to UnPublish?";
    }
    setOpenPublishDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeleteDialogAgree = () => {
    setOpenDeleteDialog(false);
    updateDocumentDetails();
  };

  const handleDeleteDocument = (obj, mode) => {
    elementRef.current = {
      ...obj,
      mode: mode,
      isDeleted: true,
      source: "delete",
    };
    setOpenDeleteDialog(true);
  };

  const clearFileInput = () => {
    elementFileRef.current.value = null;
    setFinalFileName(null);
    setDisplayFileName(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const getFileSourceIcon = (type) => {
    switch (type) {
      case "png":
        return <PngIcon className={classes.iconStyle} color="secondary" />;
      case "jpg":
        return <JpgIcon className={classes.iconStyle} color="secondary" />;
      case "txt":
        return <TxtIcon className={classes.iconStyle} />;
      case "docx":
        return (
          <WordIcon
            sx={{
              color: "#009688",
              width: "26px",
              height: "26px",
              cursor: "pointer",
            }}
          />
        );
      case "pdf":
        return (
          <PictureAsPdfIcon className={classes.iconStyle} color="secondary" />
        );
      case "xlsx":
        return (
          <img
            alt="excel icon"
            src="../../../../playerProfile/excel.png"
            height="23px"
            width="23px"
          />
        );
      default:
        return <TxtIcon className={classes.iconStyle} />;
    }
  };

  const handleFileClick = async (file, fileExtension) => {
    const url = gameScoreBaseUrl(file);
    if (["png", "pdf", "jpg"].includes(fileExtension)) {
      //window.open(url, "_blank");
      openFileInNewTab(url, file);
    } else {
      await fetch(url).then((response) =>
        response.blob().then((blob) => {
          let url = window.URL.createObjectURL(new Blob([blob]));
          let a = document.createElement("a");
          a.href = url;
          a.download = file;
          a.click();
          window.URL.revokeObjectURL(url);
        })
      );
    }
  };

  const openFileInNewTab = async (url) => {
    await fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const fileUrl = window.URL.createObjectURL(blob);
        const newTab = window.open(fileUrl, "_blank");

        // Revoke the object URL when the new tab is closed
        newTab.addEventListener("unload", () => {
          window.URL.revokeObjectURL(fileUrl);
        });
      });
  };

  return (
    <>
      <Loader isLoading={loading} />
      <AppBar position="static" className={classes.appbarBackground}>
        <Toolbar>
          <Typography
            variant="h6"
            sx={{ fontSize: "clamp(1rem, 2vw, 1.5rem)" }}
          >
            Box Score Dashboard
          </Typography>
        </Toolbar>
      </AppBar>
      <Container maxWidth={false}>
        {/* Upload Game Score Document Section */}
        {userAppRoles.includes(Role.Admin) ? (
          <Paper elevation={3} sx={{ my: 2 }}>
            <Box>
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="row"
                  className={classes.headerBox}
                >
                  <Box display="flex" pl={2}>
                    <Typography className={`${classes.headerText} headerTitle`}>
                      Upload Document
                    </Typography>
                  </Box>
                </Box>

                {/* <Divider sx={{ marginTop: "1rem", marginBottom: "1rem" }} /> */}
                <Box p={2}>
                  <input
                    type="file"
                    onChange={uploadDocumentChangeHandler}
                    className={classes.fileInput}
                    ref={elementFileRef}
                    accept="image/png,image/jpeg,application/pdf,.xlsx,.doc,.docx,.txt"
                  />
                  <Divider sx={{ marginTop: "1rem", marginBottom: "1rem" }} />
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      component="label"
                      onClick={() => uploadDocumentHandler(finalFileName)}
                      startIcon={<CloudUploadIcon />}
                      size="small"
                    >
                      Upload
                    </Button>
                    <Button
                      variant="contained"
                      component="label"
                      onClick={() => clearFileInput()}
                      startIcon={<ClearIcon />}
                      size="small"
                      color="error"
                    >
                      Clear
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </Box>
          </Paper>
        ) : null}

        {/* View Game Score Documents Section */}
        <Paper elevation={3} sx={{ my: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            flexDirection="row"
            className={classes.headerBox}
          >
            <Box display="flex" pl={2}>
              <Typography className={`${classes.headerText} headerTitle`}>
                View Documents
              </Typography>
            </Box>
          </Box>

          <Box p={2}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableCell sx={{ padding: "0.2rem" }}>
                    Document Name
                  </TableCell>
                  <TableCell sx={{ padding: "0.2rem" }}>Updated On</TableCell>
                  {user && user.isAdmin ? (
                    <TableCell sx={{ padding: "0.2rem" }}>Publish</TableCell>
                  ) : null}

                  <TableCell sx={{ padding: "0.2rem" }}></TableCell>
                </TableHead>
                <TableBody>
                  {documentList.length > 0 ? (
                    documentList
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((document, index) => {
                        const {
                          docId,
                          docType,
                          fileExtension,
                          documentName,
                          displayName,
                          isPublished,
                          createdDate,
                        } = document;
                        return (
                          <TableRow key={index} className={classes.tableRow}>
                            <TableCell style={{ padding: "0.2rem" }}>
                              {displayName}
                            </TableCell>
                            <TableCell style={{ padding: "0.2rem" }}>
                              {Helper.FormatDate(moment(createdDate).format())}
                            </TableCell>

                            {user && user.isAdmin ? (
                              <TableCell style={{ padding: "0.2rem" }}>
                                <Checkbox
                                  inputProps={{
                                    "aria-label": "controlled",
                                  }}
                                  checked={isPublished ? true : false}
                                  onChange={() =>
                                    handlePublishDocument(document, "P")
                                  }
                                />
                              </TableCell>
                            ) : null}

                            <TableCell style={{ padding: "0.2rem" }}>
                              <Stack direction="row" spacing={2}>
                                <Box>
                                  {/* <a
                                        href={gameScoreBaseUrl(documentName)}
                                        download
                                        target="_blank"
                                      >
                                        {getFileSourceIcon(fileExtension)}
                                      </a> */}
                                  <Button
                                    onClick={() =>
                                      handleFileClick(
                                        documentName,
                                        fileExtension
                                      )
                                    }
                                  >
                                    {getFileSourceIcon(fileExtension)}
                                  </Button>
                                </Box>

                                {user && user.isAdmin ? (
                                  <Box>
                                    <DeleteOutlineIcon
                                      sx={{
                                        color: "red",
                                        width: "26px",
                                        height: "26px",
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        handleDeleteDocument(document, "D")
                                      }
                                    />
                                  </Box>
                                ) : null}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No Records Found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10]}
              component="div"
              count={
                documentList && documentList.length > 0
                  ? documentList.length
                  : 0
              }
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ width: "100%" }}
            />
          </Box>
        </Paper>

        {/* Publish Document Dialog Box */}
        <Dialog
          open={openPublishDialog}
          TransitionComponent={Transition}
          keepMounted
          onClose={handleDialogClose}
          aria-describedby="alert-dialog-slide-description"
          maxWidth="lg"
          PaperProps={{
            style: {
              width: "40%",
            },
          }}
        >
          <DialogTitle>Confirmation</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-publish-dialog-slide-description">
              {publishMsgRef.current}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleDialogClose}
              variant="contained"
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDialogAgree}
              variant="contained"
              color="primary"
              autoFocus
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Document Delete Dialog Box */}
        <Dialog
          open={openDeleteDialog}
          TransitionComponent={Transition}
          keepMounted
          onClose={handleDeleteDialogClose}
          aria-describedby="alert-delete-dialog-slide-description"
          maxWidth="lg"
          PaperProps={{
            style: {
              width: "40%",
            },
          }}
        >
          <DialogTitle>Confirmation</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-slide-description">
              Do you wish to delete?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleDeleteDialogClose}
              variant="contained"
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteDialogAgree}
              variant="contained"
              color="primary"
              autoFocus
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Common Snack bar for all tabs */}
        <Snackbar
          open={alert}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={3000}
          onClose={() => setAlert(false)}
        >
          <Alert
            onClose={() => setAlert(false)}
            severity={alertType}
            sx={{ width: "100%" }}
          >
            {alertMessage}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default GameScoreDashboard;
